import { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Clock, Banknote, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RecruitmentPage() {
  const { t } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/recruitments/published`).then(r => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleApply = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setSelectedJob(null); setForm({ name: '', email: '', message: '' }); }, 3000);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">{t('contentPages.legal.loading')}</div>;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="recruitment-page">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{t('contentPages.recruitment.title')}</h1>
          <p className="text-gray-500">{t('contentPages.recruitment.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {jobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-400">
            {t('contentPages.recruitment.noData')}
          </div>
        ) : (
          <div className="space-y-6">
            {jobs.map(job => (
              <div key={job.id} className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">{job.title}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    {job.location && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="h-4 w-4 text-[#00A651]" />{job.location}
                      </div>
                    )}
                    {job.salary && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Banknote className="h-4 w-4 text-[#00A651]" />{job.salary}
                      </div>
                    )}
                    {job.workHours && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="h-4 w-4 text-[#00A651]" />{job.workHours}
                      </div>
                    )}
                  </div>
                  {job.requirements && (
                    <div className="mb-3">
                      <h3 className="text-sm font-semibold text-gray-700 mb-1">{t('contentPages.recruitment.requirements')}</h3>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{job.requirements}</p>
                    </div>
                  )}
                  {job.description && (
                    <p className="text-sm text-gray-600 whitespace-pre-wrap mb-4">{job.description}</p>
                  )}
                  <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={() => setSelectedJob(job)}>
                    <Send className="h-4 w-4 mr-2" />{t('contentPages.recruitment.apply')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedJob && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedJob(null)}>
            <div className="bg-white rounded-lg p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
              {submitted ? (
                <div className="text-center py-8">
                  <div className="text-[#00A651] text-5xl mb-4">&#10003;</div>
                  <h3 className="text-lg font-bold">{t('contentPages.recruitment.applicationReceived')}</h3>
                  <p className="text-gray-500 mt-2">{t('contentPages.recruitment.willContact')}</p>
                </div>
              ) : (
                <form onSubmit={handleApply}>
                  <h3 className="text-lg font-bold mb-4">{`${selectedJob.title}${t('contentPages.recruitment.applyFor')}`}</h3>
                  <div className="space-y-4">
                    <div><Label>{t('contentPages.recruitment.name')}</Label><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1" /></div>
                    <div><Label>{t('contentPages.recruitment.email')}</Label><Input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1" /></div>
                    <div><Label>{t('contentPages.recruitment.message')}</Label><Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={3} className="mt-1" /></div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button type="button" variant="outline" onClick={() => setSelectedJob(null)} className="flex-1">{t('contentPages.recruitment.cancel')}</Button>
                    <Button type="submit" className="flex-1 bg-[#00A651] hover:bg-[#008a43]">{t('contentPages.recruitment.send')}</Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
